import React, { useState, useEffect } from "react";
import { DiscordAlert } from "@/api/entities";
import { invokeFunctions } from "@/api/integrations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, TestTube, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const EVENTOS = [
  { key: "lead_manual", label: "Lead Manual", description: "Cuando se crea un lead manualmente" },
  { key: "lead_web", label: "Lead desde Web", description: "Cuando llega un lead desde la web" },
  { key: "presupuesto_generado", label: "Presupuesto Generado", description: "Cuando se genera un nuevo presupuesto" },
  { key: "presupuesto_email", label: "Presupuesto Enviado por Email", description: "Cuando se envía un presupuesto por email" },
  { key: "presupuesto_erp", label: "Presupuesto Enviado a ERP", description: "Cuando se envía un presupuesto al ERP" },
  { key: "cambio_estado", label: "Cambio de Estado", description: "Cuando un lead cambia de estado" },
  { key: "lead_sin_respuesta", label: "Lead Sin Respuesta", description: "Lead sin respuesta en X días" },
  { key: "error_webhook", label: "Error en Webhook", description: "Cuando falla un webhook" }
];

function EventoCard({ evento, alerta, onSave }) {
  const { toast } = useToast();
  const [url, setUrl] = useState(alerta?.webhook_url || "");
  const [activo, setActivo] = useState(alerta?.activo || false);
  const [testando, setTestando] = useState(false);

  useEffect(() => {
    setUrl(alerta?.webhook_url || "");
    setActivo(alerta?.activo || false);
  }, [alerta]);

  const handleTest = async () => {
    setTestando(true);
    try {
      const response = await invokeFunctions('sendDiscordAlert', {
        evento: evento.key,
        lead_id: null,
        datos_extra: { test: true }
      });
      
      toast({
        title: "Test enviado",
        description: "Revisa tu canal de Discord",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar el test",
        variant: "destructive"
      });
    }
    setTestando(false);
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-gray-900 dark:text-white">{evento.label}</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{evento.description}</p>
            </div>
          </div>
          <Switch
            checked={activo}
            onCheckedChange={(checked) => {
              setActivo(checked);
              if (url) {
                onSave({ evento: evento.key, webhook_url: url, activo: checked });
              }
            }}
          />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor={`url-${evento.key}`} className="dark:text-gray-300">Webhook URL</Label>
          <Input
            id={`url-${evento.key}`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
            className="dark:bg-gray-800 dark:border-gray-700"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => {
              if (url) {
                onSave({ evento: evento.key, webhook_url: url, activo });
              }
            }}
            disabled={!url}
            size="sm"
            variant="outline"
          >
            Guardar
          </Button>
          <Button
            onClick={handleTest}
            disabled={!url || !activo || testando}
            size="sm"
            variant="outline"
          >
            {testando ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <TestTube className="w-4 h-4 mr-2" />
                Test
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Discord() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: alertas = [] } = useQuery({
    queryKey: ['discord_alerts'],
    queryFn: () => DiscordAlert.list(),
  });

  const createOrUpdateMutation = useMutation({
    mutationFn: async ({ evento, webhook_url, activo }) => {
      const existente = alertas.find(a => a.evento === evento);
      if (existente) {
        return DiscordAlert.update(existente.id, { webhook_url, activo });
      } else {
        return DiscordAlert.create({ evento, webhook_url, activo });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discord_alerts'] });
      toast({
        title: "Guardado",
        description: "La configuración se ha guardado correctamente",
      });
    },
  });

  const getAlerta = (evento) => {
    return alertas.find(a => a.evento === evento);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Alertas Discord</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configura notificaciones en Discord para eventos importantes
        </p>
      </div>

      <div className="space-y-4">
        {EVENTOS.map((evento) => (
          <EventoCard
            key={evento.key}
            evento={evento}
            alerta={getAlerta(evento.key)}
            onSave={(data) => createOrUpdateMutation.mutate(data)}
          />
        ))}
      </div>
    </div>
  );
}